import { useState } from 'react';
import clsx from 'clsx';
import { StatusPill } from './StatusPill';
import { statusSemantic } from '../lib/colors';
import type { MockPhase, MockUpdate } from '../lib/mockData';

/**
 * PhaseCard — collapsible phase container for the v2 portal.
 *
 * Default state: collapsed (just the header row). Click anywhere on the
 * header to expand. Inside the expanded body: optional phase narrative
 * (rolling client-facing summary), tasks (grouped), permits, and updates
 * from this period.
 *
 * Photos have been removed — they live in a dedicated /photos page now.
 *
 * Tasks counts and progress show in the header so a client can scan all
 * 11 phases at once before expanding any. Progress bar sized at 8px (was
 * 4px) to match the internal app's overview.
 */
export function PhaseCard({ phase }: { phase: MockPhase }) {
  const [open, setOpen] = useState(false);

  const completedTasks = phase.tasks.filter((t) => t.is_complete).length;
  const totalTasks = phase.tasks.length;
  const sem = statusSemantic(phase.status);

  return (
    <article className="border border-[var(--color-border)] rounded-md bg-[var(--color-surface)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={clsx(
          'group w-full flex items-center gap-3 px-4 py-3 text-left',
          'hover:bg-[var(--color-surface-2)]/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--color-accent)]',
          'transition-colors',
        )}
      >
        <span className="num label-mono shrink-0 text-[var(--color-ink-2)]">
          {String(phase.phase_number).padStart(2, '0')}
        </span>

        <span className="text-base font-semibold text-[var(--color-ink)] truncate">
          {phase.phase_name}
        </span>

        <span className="flex-1" />

        {/* Task count + bold progress bar — sized for prominent visual scan */}
        <span className="hidden sm:flex items-center gap-3 shrink-0">
          <span className="text-xs text-[var(--color-ink-2)] num">
            {completedTasks}/{totalTasks} tasks
          </span>
          <span className="flex items-center gap-2.5">
            <span className="w-48 h-3.5 rounded-full bg-[var(--color-surface-2)] overflow-hidden border border-[var(--color-border-strong)] shadow-inner">
              <span
                className={clsx(
                  'block h-full transition-all',
                  sem === 'success' ? 'bg-[var(--color-success)]' :
                  sem === 'warning' ? 'bg-[var(--color-warning)]' :
                  sem === 'danger'  ? 'bg-[var(--color-danger)]'  :
                  'bg-[var(--color-ink-3)]'
                )}
                style={{ width: `${Math.max(0, Math.min(100, phase.percent_complete))}%` }}
              />
            </span>
            <span className="text-base font-bold tabular-nums text-[var(--color-ink)] w-11 text-right">
              {phase.percent_complete}%
            </span>
          </span>
        </span>

        <StatusPill status={phase.status} size="xs" />

        {/* Big can't-miss expand affordance — circled chevron with hover lift.
            Sized at 36px so it's a clear touch target on mobile too. */}
        <span
          className={clsx(
            'shrink-0 ml-1 inline-flex items-center justify-center rounded-full',
            'border-2 transition-all duration-200',
            open
              ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
              : 'border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-ink-2)] group-hover:border-[var(--color-accent)] group-hover:text-[var(--color-accent)]',
          )}
          style={{ width: '36px', height: '36px', fontSize: '20px', lineHeight: 1 }}
          aria-hidden="true"
        >
          <span
            className={clsx('transition-transform duration-200 font-bold', open ? 'rotate-180' : '')}
          >
            ▾
          </span>
        </span>
      </button>

      {open && (
        <div className="border-t border-[var(--color-border)] px-4 py-4 flex flex-col gap-5 bg-[var(--color-surface-2)]/30">
          {/* Phase target dates */}
          {(phase.target_start_date || phase.target_end_date) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--color-ink-2)]">
              {phase.target_start_date && (
                <span>
                  <span className="label-mono mr-2">Target start</span>
                  <span className="num">{formatDate(phase.target_start_date)}</span>
                </span>
              )}
              {phase.target_end_date && (
                <span>
                  <span className="label-mono mr-2">Target end</span>
                  <span className="num">{formatDate(phase.target_end_date)}</span>
                </span>
              )}
              {phase.actual_start_date && (
                <span>
                  <span className="label-mono mr-2">Started</span>
                  <span className="num">{formatDate(phase.actual_start_date)}</span>
                </span>
              )}
            </div>
          )}

          {/* Optional phase narrative — single rolling summary the PM curates */}
          {phase.narrative && (
            <div
              className="text-sm leading-relaxed text-[var(--color-ink)] border-l-2 border-[var(--color-accent)] pl-3 py-1 italic"
            >
              {phase.narrative}
            </div>
          )}

          {/* Tasks grouped */}
          {phase.tasks.length > 0 && (
            <Section label="Tasks">
              <TaskList tasks={phase.tasks} />
            </Section>
          )}

          {/* Permits */}
          {phase.permits.length > 0 && (
            <Section label="Permits">
              <ul className="flex flex-col gap-2">
                {phase.permits.map((p) => (
                  <li
                    key={p.id}
                    className="border border-[var(--color-border)] rounded bg-[var(--color-surface)] px-3 py-2"
                  >
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <p className="text-sm font-medium text-[var(--color-ink)]">{p.permit_type}</p>
                      <StatusPill status={p.status} size="xs" />
                    </div>
                    <p className="text-xs text-[var(--color-ink-2)] mt-0.5">
                      {p.jurisdiction}
                      {p.permit_number && (
                        <>
                          <span className="mx-2">·</span>
                          <span className="num">#{p.permit_number}</span>
                        </>
                      )}
                    </p>
                    {p.published_summary && (
                      <p className="text-xs text-[var(--color-ink)] mt-1">{p.published_summary}</p>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Updates from this phase — both pinned and unpinned, uniform compact treatment.
              Pinned ones get a tiny 📌 chip so you can spot them. */}
          {phase.updates.length > 0 && (
            <Section label="Updates from this phase">
              <ol className="flex flex-col gap-3">
                {phase.updates
                  .slice()
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((u) => (
                    <PhaseUpdateRow key={u.id} update={u} />
                  ))}
              </ol>
            </Section>
          )}

          {phase.tasks.length === 0 && phase.permits.length === 0 && phase.updates.length === 0 && (
            <p className="text-xs text-[var(--color-ink-3)] italic">
              This phase hasn&apos;t started yet — content will appear here as work begins.
            </p>
          )}
        </div>
      )}
    </article>
  );
}

function PhaseUpdateRow({ update }: { update: MockUpdate }) {
  return (
    <li className="border-l-2 border-[var(--color-border)] pl-3">
      <div className="flex items-baseline gap-2 mb-0.5 flex-wrap">
        <span className="num text-xs text-[var(--color-ink-2)]">{formatDate(update.date)}</span>
        <span className="label-mono">{update.log_type}</span>
        {update.pinned && (
          <span
            className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
            style={{
              background: 'var(--color-accent-soft)',
              color: 'var(--color-accent)',
            }}
            title="Pinned to Headlines at top of page"
          >
            📌 Pinned
          </span>
        )}
      </div>
      <p className="text-sm text-[var(--color-ink)] leading-relaxed">{update.body}</p>
    </li>
  );
}

function Section({
  label,
  right,
  children,
}: {
  label: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <p className="label-mono">{label}</p>
        {right}
      </div>
      {children}
    </div>
  );
}

function TaskList({ tasks }: { tasks: MockPhase['tasks'] }) {
  // Group by task_group preserving insertion order
  const groups = new Map<string, typeof tasks>();
  for (const t of tasks) {
    const k = t.task_group || '';
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(t);
  }

  return (
    <div className="flex flex-col gap-3">
      {Array.from(groups.entries()).map(([group, groupTasks]) => (
        <div key={group || '__none'}>
          {group && (
            <p className="text-[11px] font-semibold tracking-wide uppercase text-[var(--color-ink-2)] mb-1.5">
              {group}
            </p>
          )}
          <ul className="flex flex-col gap-1.5">
            {groupTasks.map((t) => (
              <TaskRow key={t.id} task={t} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * TaskRow — completion treatment without strikethrough.
 * - Filled green check box for done tasks; empty bordered box for open
 * - Task text stays full color and full readable in both states
 * - Completed tasks have a subtle green left-border accent for peripheral signal
 * - Date appears as a small chip after the text — never crossed out
 */
function TaskRow({ task }: { task: MockPhase['tasks'][number] }) {
  return (
    <li
      className={clsx(
        'flex items-start gap-2.5 text-sm pl-2',
        task.is_complete ? 'border-l-2 border-[var(--color-success)]' : 'border-l-2 border-transparent',
      )}
    >
      {task.is_complete ? (
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded mt-0.5 shrink-0 text-[10px] font-bold text-white"
          style={{ background: 'var(--color-success)' }}
          aria-label="complete"
        >
          ✓
        </span>
      ) : (
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded border mt-0.5 shrink-0 bg-[var(--color-surface)] border-[var(--color-border-strong)]"
          aria-label="not complete"
        />
      )}
      <span className="flex-1 min-w-0 flex items-baseline flex-wrap gap-1.5">
        <span className="text-[var(--color-ink)]">{task.task_text}</span>

        {task.is_critical && !task.is_complete && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--color-danger)] font-bold">
            critical
          </span>
        )}
        {task.completed_date && task.is_complete && (
          <span
            className="num text-[11px] px-1.5 py-0.5 rounded"
            style={{
              background: 'var(--color-success-soft)',
              color: 'var(--color-success)',
            }}
            title="Completed on this date"
          >
            ✓ {formatDate(task.completed_date)}
          </span>
        )}
        {!task.is_complete && task.due_date && (
          <span
            className="num text-[11px] px-1.5 py-0.5 rounded"
            style={{
              background: 'var(--color-warning-soft)',
              color: 'var(--color-warning)',
            }}
            title="Target date"
          >
            due {formatDate(task.due_date)}
          </span>
        )}
      </span>
    </li>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
